import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { Sparkles } from '@/assets/icons';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';

/** What a Pro-only feature the visitor ran into is called, so the dialog can say which one. */
export type UpgradeReason = 'pattern-composer';

function reasonCopy(reason: UpgradeReason, t: TFunction): { title: string; body: string } {
    switch (reason) {
        case 'pattern-composer':
            return { title: t('billing.upgrade.patternComposer.title'), body: t('billing.upgrade.patternComposer.body') };
        default: {
            const _exhaustive: never = reason;
            return _exhaustive;
        }
    }
}

/**
 * The foot of both plan dialogs: Dismiss, and either checkout or the line that
 * Pro can't be bought yet. `checkoutHref` is null when the backend has no
 * checkout for this deploy (or the URL couldn't be built safely); the dialog
 * then offers nothing to buy and says so, rather than a button that goes nowhere.
 */
export function CheckoutOffer({
    checkoutHref,
    labels,
    onDismiss,
}: {
    checkoutHref: string | null;
    labels: { dismiss: string; checkout: string; unavailable: string };
    onDismiss: () => void;
}) {
    return (
        <div className="flex flex-wrap items-center justify-end gap-2">
            {checkoutHref == null && <p className="mr-auto text-sm text-muted-foreground">{labels.unavailable}</p>}
            <Button type="button" variant="outline" onClick={onDismiss}>
                {labels.dismiss}
            </Button>
            {checkoutHref != null && (
                <Button asChild>
                    <a href={checkoutHref} rel="noreferrer">
                        <Sparkles aria-hidden="true" />
                        {labels.checkout}
                    </a>
                </Button>
            )}
        </div>
    );
}

/**
 * Shown when a free account reaches for a Pro-only feature. Navigation-dumb in
 * the way the `/try` sign-up wall is: the provider decides when it opens and
 * where checkout goes.
 */
export function UpgradeDialog({
    open,
    reason,
    checkoutHref,
    onDismiss,
}: {
    open: boolean;
    reason: UpgradeReason;
    checkoutHref: string | null;
    onDismiss: () => void;
}) {
    const { t } = useTranslation();
    const copy = reasonCopy(reason, t);
    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                if (!next) onDismiss();
            }}
        >
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{copy.title}</DialogTitle>
                    <DialogDescription>{copy.body}</DialogDescription>
                </DialogHeader>
                <CheckoutOffer
                    checkoutHref={checkoutHref}
                    labels={{
                        dismiss: t('billing.upgrade.dismiss'),
                        checkout: t('billing.upgrade.checkout'),
                        unavailable: t('billing.upgrade.proUnavailable'),
                    }}
                    onDismiss={onDismiss}
                />
            </DialogContent>
        </Dialog>
    );
}
